import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { RabbitCard } from "../components/RabbitCard";
import { WaitlistForm } from "../components/WaitlistForm";
import { ArrowRight, Check } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1546977682-2ef6aee8e5fa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const ACCENT_IMG = "https://images.unsplash.com/photo-1606943095508-0cd76628fda3?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api.get("/rabbits", { params: { status: "available" } })
      .then((r) => setFeatured(r.data.slice(0, 3)))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="grain-bg relative overflow-hidden border-b border-border bg-cream">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-20 lg:grid-cols-12 lg:gap-16 lg:px-12 lg:py-32">
          <div className="lg:col-span-6 flex flex-col justify-center animate-fade-up">
            <div className="section-label">Northeast Alabama · Pedigreed Rabbitry</div>
            <h1 className="mt-6 font-serif text-5xl leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl">
              Heirloom <em className="text-terracotta">Lion's Mane</em><br />
              rabbits, raised<br />
              the slow way.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-stone">
              We breed a small handful of pedigreed Lion's Mane rabbits each year — handled daily, health-checked twice, and ready to come home to families across Alabama.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/rabbits"
                data-testid="hero-cta-view-rabbits"
                className="group inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3.5 text-sm tracking-wide text-white transition-transform hover:-translate-y-0.5"
              >
                View Available Rabbits
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/care-guide"
                data-testid="hero-cta-care-guide"
                className="link-underline text-sm tracking-wide text-foreground/80"
              >
                Read the Care Guide →
              </Link>
            </div>
            <div className="mt-12 grid max-w-md grid-cols-3 gap-6 border-t border-border pt-8">
              <div><div className="font-serif text-3xl text-terracotta">2026</div><div className="text-xs text-stone">Year Est.</div></div>
              <div><div className="font-serif text-3xl text-terracotta">ARBA</div><div className="text-xs text-stone">Standard</div></div>
              <div><div className="font-serif text-3xl text-terracotta">100%</div><div className="text-xs text-stone">Guarantee</div></div>
            </div>
          </div>
          <div className="lg:col-span-6 relative">
            <div className="arched-mask aspect-[3/4] w-full max-w-md mx-auto bg-muted">
              <img src={HERO_IMG} alt="Fluffy Lion's Mane rabbit" className="h-full w-full object-cover" />
            </div>
            <div className="absolute -bottom-6 left-4 hidden border border-border bg-white px-5 py-3 lg:block">
              <div className="text-xs uppercase tracking-[0.2em] text-sage">Now booking</div>
              <div className="font-serif text-xl">Spring 2026 Litters</div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-12">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="section-label">Why our rabbitry</div>
            <h2 className="mt-4 font-serif text-4xl leading-tight sm:text-5xl">A small herd, treated like family.</h2>
          </div>
          <div className="lg:col-span-8 grid gap-8 sm:grid-cols-2">
            {[
              { t: "Pedigreed bloodlines", d: "Three generations of recorded lineage on every kit, with ARBA-conforming type and mane."},
              { t: "Health-first breeding", d: "Two veterinary checks before pickup, fecal screening, and a 72-hour health guarantee."},
              { t: "Daily handling", d: "Every kit is gently socialized from week two — calm, curious, and family-ready."},
              { t: "Alabama legal & local", d: "Operating in compliance with Alabama Dept. of Agriculture. Local pickup, no shipping."},
            ].map((b) => (
              <div key={b.t} className="border-t border-border pt-6">
                <div className="flex items-start gap-3">
                  <Check className="mt-1 text-terracotta" size={18} />
                  <div>
                    <div className="font-serif text-2xl">{b.t}</div>
                    <p className="mt-2 text-sm leading-relaxed text-stone">{b.d}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="border-y border-border bg-[#F0EDE5]/60 py-24" data-testid="home-featured">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="mb-12 flex items-end justify-between gap-6">
            <div>
              <div className="section-label">This season</div>
              <h2 className="mt-3 font-serif text-4xl sm:text-5xl">Currently available</h2>
            </div>
            <Link to="/rabbits" className="link-underline hidden text-sm sm:inline" data-testid="home-view-all">View all →</Link>
          </div>
          {featured.length === 0 ? (
            <div className="border border-border bg-white p-12 text-center text-stone">
              New litters announced soon. <Link to="/contact" className="text-terracotta link-underline">Join the waitlist →</Link>
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {featured.map((r, i) => <RabbitCard key={r.id} rabbit={r} idx={i} />)}
            </div>
          )}
        </div>
      </section>

      {/* CARE TEASER */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-12">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="arched-mask aspect-square overflow-hidden">
              <img src={ACCENT_IMG} alt="Lion's Mane rabbit" className="h-full w-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="section-label">The breed</div>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl">What makes a Lion's Mane different.</h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-stone">
              Recognized by the British Rabbit Council and accepted by many U.S. clubs, the Lion's Mane is a compact, friendly rabbit — 4–6 lbs at maturity — distinguished by its dramatic wool mane around the head and chest. They thrive indoors, love quiet attention, and live 8–10 years.
            </p>
            <Link to="/care-guide" className="mt-8 inline-block rounded-full border border-bark px-6 py-3 text-sm text-bark transition hover:bg-bark hover:text-cream" data-testid="home-care-cta">
              Full care guide →
            </Link>
          </div>
        </div>
      </section>

      {/* WAITLIST */}
      <section className="border-t border-border bg-bark py-24 text-cream" data-testid="home-waitlist">
        <div className="mx-auto max-w-4xl px-6 text-center lg:px-12">
          <div className="section-label" style={{ color: "#C86A53" }}>Litter waitlist</div>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl">Be first to know about new kits.</h2>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-cream/75">
            We breed just a handful of litters each year and they go fast. Drop your email and we'll send a heads-up the moment new kits are weaned and ready to reserve.
          </p>
          <div className="mx-auto mt-10 max-w-md">
            <WaitlistForm testIdPrefix="home-waitlist" />
          </div>
          <p className="mt-4 text-xs text-cream/50">No spam. Unsubscribe any time.</p>
        </div>
      </section>
    </div>
  );
}
